import {useCallback, useEffect, useRef, useState, type DragEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {
  composeCreativeAsset,
  ensureCreativeFonts,
  type CreativeAssetState,
  type CreativeStatePatch,
} from '../../../common/utils/creativeAssetRenderer'
import {
  CREATIVE_PREVIEW_WIDTH,
  MAX_CREATIVE_SCREENSHOTS,
  getPlacementSpec,
  type CreativeAnimationSettings,
  type CreativePlacementId,
  type CreativePlacementSpec,
} from '../../../common/constants/creativeAssetSpecs'
import {trackEvent} from '../../../common/analytics/trackEvent'
import SafeAreaOverlay from './SafeAreaOverlay'
import {safeAreaStyle} from './safeAreaStyle'
import {useCreativeImageLoader} from './useCreativeImageLoader'
import {useImageDropTarget} from './useImageDropTarget'

type DropZone = 'content' | 'background'

export interface CreativeVideoPreview {
  animation: CreativeAnimationSettings
  durationSec: number
}

interface CreativeCanvasProps {
  state: CreativeAssetState
  placement: CreativePlacementId
  showSafeArea: boolean
  /** Set in video mode: the preview then plays the seamless loop live. */
  video?: CreativeVideoPreview
  onChange: (patch: CreativeStatePatch) => void
}

/** Pointer inside the safe-area rectangle = content drop, anywhere else = background drop. */
const zoneAt = (e: DragEvent<HTMLElement>, spec: CreativePlacementSpec, rect: DOMRect): DropZone => {
  const px = ((e.clientX - rect.left) / rect.width) * spec.width
  const py = ((e.clientY - rect.top) / rect.height) * spec.height
  const {safeArea} = spec
  const left = (spec.width - safeArea.width) / 2
  const top = (spec.height - safeArea.height) / 2 + safeArea.offsetY
  const inside = px >= left && px <= left + safeArea.width && py >= top && py <= top + safeArea.height
  return inside ? 'content' : 'background'
}

/**
 * Live scaled preview — same renderer as the export, only at preview resolution. Dropping an image on the safe area
 * fills the slot the active template uses; dropping it outside the safe area sets the background image.
 */
const CreativeCanvas = ({state, placement, showSafeArea, video, onChange}: CreativeCanvasProps) => {
  const {t} = useTranslation()
  const wrapRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [zone, setZone] = useState<DropZone>('content')
  // Canvas text does not trigger web-font loading; redraw the preview once the fonts are in.
  const [fontsReady, setFontsReady] = useState(false)
  const spec = getPlacementSpec(placement)
  const scale = CREATIVE_PREVIEW_WIDTH / spec.width

  useEffect(() => {
    let cancelled = false
    void ensureCreativeFonts().then(() => {
      if (!cancelled) setFontsReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // One persistent canvas; stills redraw on change, video mode redraws every animation frame.
  useEffect(() => {
    const host = wrapRef.current?.querySelector('.caa-canvas-host')
    if (!host) return
    if (!canvasRef.current) {
      const canvas = document.createElement('canvas')
      canvas.className = 'caa-canvas'
      canvas.setAttribute('role', 'img')
      canvasRef.current = canvas
      host.replaceChildren(canvas)
    }
    const canvas = canvasRef.current
    canvas.setAttribute('aria-label', t('creativeAssets.canvas.alt'))
    if (!video) {
      composeCreativeAsset(state, placement, scale, undefined, canvas)
      return
    }
    let rafId = 0
    const start = performance.now()
    const tick = (now: number) => {
      const loopMs = video.durationSec * 1000
      composeCreativeAsset(
        state,
        placement,
        scale,
        {animation: video.animation, t: ((now - start) % loopMs) / loopMs},
        canvas,
      )
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [state, placement, scale, video, t, fontsReady])

  // keyArt → key art, iconHeadline/iconStack → icon, gameplay/splitText → first screenshot, others → next free screenshot (replaces the last when full).
  const handleContentDropped = useCallback(
    (image: HTMLImageElement) =>
      onChange(({template, screenshots}) => {
        if (template === 'keyArt') return {keyArt: image}
        if (template === 'iconHeadline' || template === 'iconStack') return {icon: image}
        if (template === 'gameplay' || template === 'splitText') return {screenshots: [image, ...screenshots.slice(1)]}
        return {screenshots: [...screenshots.slice(0, MAX_CREATIVE_SCREENSHOTS - 1), image]}
      }),
    [onChange],
  )
  const handleBackgroundDropped = useCallback(
    (image: HTMLImageElement) => onChange({bgMode: 'image', bgImage: image}),
    [onChange],
  )

  // The zone is resolved at drop time and stored so the loader's async callback knows where to put the image.
  const dropZoneRef = useRef<DropZone>('content')
  const handleImageLoaded = useCallback(
    (image: HTMLImageElement) => {
      if (dropZoneRef.current === 'background') handleBackgroundDropped(image)
      else handleContentDropped(image)
    },
    [handleBackgroundDropped, handleContentDropped],
  )

  const {busy, error, loadFile} = useCreativeImageLoader('canvas', handleImageLoaded)

  const handleHover = useCallback(
    (e: DragEvent<HTMLElement>) => {
      const rect = wrapRef.current?.getBoundingClientRect()
      if (rect) setZone(zoneAt(e, spec, rect))
    },
    [spec],
  )

  const handleDropFile = useCallback(
    (file: File, e: DragEvent<HTMLElement>) => {
      const rect = wrapRef.current?.getBoundingClientRect()
      const dropped = rect ? zoneAt(e, spec, rect) : 'content'
      dropZoneRef.current = dropped
      trackEvent('creative_assets_canvas_dropped', {zone: dropped})
      void loadFile(file, 'drop')
    },
    [spec, loadFile],
  )

  const {dragOver, dropProps} = useImageDropTarget(handleDropFile, handleHover)

  return (
    <div className="caa-stage" {...dropProps}>
      <div ref={wrapRef} className="caa-canvas-wrap" style={{aspectRatio: `${spec.width} / ${spec.height}`}}>
        <div className="caa-canvas-host" />
        {showSafeArea && !dragOver && <SafeAreaOverlay spec={spec} />}
        {dragOver && (
          <div className="caa-drop-layer">
            <div className={`caa-drop-bg${zone === 'background' ? ' is-active' : ''}`}>
              <span className="caa-drop-tag">{t('creativeAssets.canvas.dropBackground')}</span>
            </div>
            <div className={`caa-drop-content${zone === 'content' ? ' is-active' : ''}`} style={safeAreaStyle(spec)}>
              <span className="caa-drop-tag">{t('creativeAssets.canvas.dropContent')}</span>
            </div>
          </div>
        )}
        {busy && <div className="caa-drop-overlay">{t('creativeAssets.upload.busy')}</div>}
      </div>
      {error && <p className="cvt-error">{error}</p>}
    </div>
  )
}

export default CreativeCanvas
